import { useCallback, useRef, useState } from "react";

import { supabase } from "~/integrations/supabase/client";
import { ClientSuggestion } from "~/pages/payments/hooks/useClientSuggestions";

export const useClientSearch = () => {
  const [searchResults, setSearchResults] = useState<ClientSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const lastQueryRef = useRef<string>("");
  const lastResultsRef = useRef<ClientSuggestion[]>([]);

  const resetSearch = useCallback(() => {
    setSearchResults([]);
    lastQueryRef.current = "";
    lastResultsRef.current = [];
  }, []);

  const searchClients = useCallback(async (query: string) => {
    // Return early if query is too short
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    // If we have cached results for this exact query, use them immediately
    if (query === lastQueryRef.current && lastResultsRef.current.length > 0) {
      setSearchResults(lastResultsRef.current);
      return;
    }

    setLoading(true);

    try {
      // Normalize the query
      const normalizedQuery = query.toLowerCase().trim();

      // Split into terms for multi-term matching
      const terms = normalizedQuery
        .split(/\s+/)
        .filter((term) => term.length > 0);

      // Build query with improved tax_id matching
      let dbQuery = supabase
        .from("clients")
        .select("id, name, tax_id")
        .is("deleted_at", null); // Exclude soft-deleted clients

      // Create filter conditions array
      const filterConditions = [];

      // Priority 1: Check for exact tax_id match (with normalization)
      // This is the most specific search and should be prioritized
      const taxIdWithoutPunctuation = normalizedQuery.replace(/[.-]/g, "");
      if (taxIdWithoutPunctuation.length >= 2) {
        filterConditions.push(`tax_id.ilike.${taxIdWithoutPunctuation}%`);
        filterConditions.push(`tax_id.ilike.%${taxIdWithoutPunctuation}%`);
      }

      // Priority 2: Standard name and tax_id searches with the raw normalized query
      filterConditions.push(`name.ilike.%${normalizedQuery}%`);
      filterConditions.push(`tax_id.ilike.%${normalizedQuery}%`);

      // Priority 3: Multi-term search for better partial matching
      if (terms.length > 1) {
        terms.forEach((term) => {
          if (term.length >= 2) {
            filterConditions.push(`name.ilike.%${term}%`);
            filterConditions.push(`tax_id.ilike.%${term}%`);
          }
        });
      }

      // Use OR to combine all filter conditions
      const { data, error } = await dbQuery
        .or(filterConditions.join(","))
        .limit(15);

      if (error) throw error;

      // Format and deduplicate results
      const formattedResults: ClientSuggestion[] = [];
      const seenIds = new Set<string>();

      // Process results to avoid duplicates and ensure they're properly formed
      (data || []).forEach((client) => {
        if (!seenIds.has(client.id)) {
          seenIds.add(client.id);
          formattedResults.push({
            id: client.id,
            name: client.name,
            taxId: client.tax_id,
          });
        }
      });

      // Update refs and state
      lastQueryRef.current = query;
      lastResultsRef.current = formattedResults;
      setSearchResults(formattedResults);
    } catch (error) {
      console.error("Error searching clients:", error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { searchResults, loading, searchClients, resetSearch };
};
